import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatSoles, cartItemPrice, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Upload, MessageCircle, CheckCircle2 } from "lucide-react";

const CUSTOMER_TYPES = [
  { v: "cliente_final", l: "Cliente final" },
  { v: "decorador", l: "Decorador" },
  { v: "eventista", l: "Eventista" },
  { v: "salon", l: "Salón de recepciones" },
];

const PAY_METHODS = [
  { v: "yape", l: "Yape" },
  { v: "plin", l: "Plin" },
  { v: "transferencia", l: "Transferencia bancaria" },
  { v: "pago_entrega", l: "Pago contra entrega" },
];

function CopyField({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between bg-white border border-black/10 rounded-xl px-4 py-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{label}</div>
        <div className="font-semibold font-mono" data-testid={`${testid}-value`}>{value}</div>
      </div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success(`${label} copiado`);
        }}
        className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-[var(--brand-primary)] hover:text-white flex items-center justify-center transition"
        data-testid={`${testid}-copy`}
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

export default function Checkout() {
  const { cart, cartTotals, settings, clearCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_type: "cliente_final",
    customer_district: "",
    payment_method: "yape",
    shipping_option: "",
    notes: "",
  });
  const [receiptPath, setReceiptPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleReceipt(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "receipts");
    setUploading(true);
    try {
      const { data } = await http.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptPath(data.path);
      toast.success("Comprobante subido");
    } catch (err) {
      toast.error("Error al subir comprobante");
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    if (!form.customer_name || !form.customer_phone) {
      toast.error("Completa nombre y WhatsApp");
      return;
    }
    if (cart.length === 0) {
      toast.error("Carrito vacío");
      return;
    }
    const items = cart.map((i) => {
      const unit = cartItemPrice(i);
      return {
        product_id: i.product_id,
        code: i.code,
        name: i.name,
        qty: i.qty,
        unit_price: unit,
        subtotal: unit * i.qty,
        variant: i.variant || null,
        color: i.color || null,
      };
    });
    setSubmitting(true);
    try {
      const { data } = await http.post("/orders", {
        ...form,
        items,
        total: cartTotals.subtotal,
        receipt_path: receiptPath || null,
      });
      setOrder(data);
      clearCart();
      toast.success(`Pedido ${data.order_code} creado`);
    } catch (e) {
      toast.error("Error al crear pedido");
    } finally {
      setSubmitting(false);
    }
  }

  function waLink() {
    if (!order || !settings?.whatsapp_number) return "#";
    const wa = settings.whatsapp_number.replace(/\D/g, "");
    const lines = [
      `*Nuevo pedido ${order.order_code}* — Floral Rossé`,
      "",
      `Cliente: ${order.customer_name} (${CUSTOMER_TYPES.find((c) => c.v === order.customer_type)?.l})`,
      `WhatsApp: ${order.customer_phone}`,
      order.customer_district ? `Distrito: ${order.customer_district}` : "",
      "",
      "*Productos:*",
      ...order.items.map((i) => `• ${i.qty}x ${i.name}${i.variant ? ` (${i.variant})` : ""}${i.color ? ` [${i.color}]` : ""} [${i.code}] — ${formatSoles(i.unit_price)} c/u`),
      "",
      order.shipping_label ? `Entrega: ${order.shipping_label} (${formatSoles(order.shipping_cost)})` : "",
      `*Total: ${formatSoles(order.total)}*`,
      `Método: ${order.payment_method.toUpperCase()}`,
      receiptPath ? "Comprobante adjuntado en la web." : "Comprobante pendiente de envío.",
    ].filter(Boolean);
    return `https://wa.me/${wa}?text=${encodeURIComponent(lines.join("\n"))}`;
  }

  if (order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="order-success">
        <div className="bg-white rounded-3xl p-10 border border-black/5 text-center">
          <CheckCircle2 size={60} className="text-[var(--brand-accent)] mx-auto" />
          <h1 className="font-serif text-4xl font-bold mt-6">¡Pedido creado!</h1>
          <div className="mt-2 text-neutral-500">Tu número de pedido es</div>
          <div className="font-serif text-5xl font-bold text-[var(--brand-primary)] mt-2 tracking-wider" data-testid="order-code">
            {order.order_code}
          </div>
          <p className="mt-6 text-neutral-600 max-w-md mx-auto">
            {settings?.msg_order_confirmation || "Envíanos tu pedido por WhatsApp para coordinar entrega y verificar el pago."}
          </p>
          <a href={waLink()} target="_blank" rel="noreferrer">
            <Button className="mt-6 h-14 px-8 rounded-full btn-accent text-base font-semibold" data-testid="send-whatsapp-btn">
              <MessageCircle size={18} className="mr-2" /> Enviar pedido a WhatsApp
            </Button>
          </a>
          <div className="mt-10 flex gap-3 justify-center flex-wrap">
            <Link to={`/seguimiento?codigo=${order.order_code}`}><Button variant="outline" className="rounded-full" data-testid="track-order-link">Seguir mi pedido</Button></Link>
            <Link to="/"><Button variant="outline" className="rounded-full">Volver al inicio</Button></Link>
            <Link to="/catalogo"><Button variant="outline" className="rounded-full">Seguir comprando</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-4xl font-bold">Tu carrito está vacío</h1>
        <Link to="/catalogo"><Button className="btn-primary rounded-full mt-6 h-12 px-6">Explorar catálogo</Button></Link>
      </div>
    );
  }

  const pay = settings?.payment || {};
  const methods = pay.methods || { yape: true, transferencia: true, pago_entrega: true };
  const ship = settings?.shipping || {};
  const shippingOptions = [
    ...(ship.pickup_enabled ? [{ id: "pickup", name: "Recojo en tienda", cost: 0, note: [ship.pickup_address, ship.pickup_note].filter(Boolean).join(" — ") }] : []),
    ...(ship.zones || []),
  ];
  const selectedShip = shippingOptions.find((o) => o.id === form.shipping_option);
  const grandTotal = cartTotals.subtotal + Number(selectedShip?.cost || 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="checkout-page">
      <h1 className="font-serif text-4xl sm:text-5xl font-bold">Finalizar pedido</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-2xl p-6 border border-black/5">
            <h2 className="font-serif text-2xl font-semibold mb-4">Datos de contacto</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nombre completo</Label>
                <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="checkout-name" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+51 ..." data-testid="checkout-phone" />
              </div>
              <div>
                <Label>Email (opcional)</Label>
                <Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="checkout-email" />
              </div>
              <div>
                <Label>Tipo de cliente</Label>
                <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                  <SelectTrigger data-testid="checkout-customer-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Distrito / Ciudad de entrega</Label>
                <Input value={form.customer_district} onChange={(e) => set("customer_district", e.target.value)} data-testid="checkout-district" />
              </div>
              <div className="sm:col-span-2">
                <Label>Notas</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="checkout-notes" />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-6 border border-black/5">
            <h2 className="font-serif text-2xl font-semibold mb-4">Datos de pago</h2>
            {pay.instructions && <p className="text-sm text-neutral-600 mb-4" data-testid="pay-instructions">{pay.instructions}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {methods.yape && pay.yape && <CopyField label="Yape" value={pay.yape} testid="pay-yape" />}
              {methods.plin && pay.plin && <CopyField label="Plin" value={pay.plin} testid="pay-plin" />}
              {methods.transferencia && pay.account && <CopyField label={`Cuenta ${pay.bank || "BCP"}`} value={pay.account} testid="pay-account" />}
              {methods.transferencia && pay.cci && <CopyField label="CCI" value={pay.cci} testid="pay-cci" />}
              {methods.transferencia && (pay.accounts || []).map((a, i) => (
                <CopyField key={i} label={`${a.bank || "Cuenta"}${a.cci ? " · CCI " + a.cci : ""}`} value={a.account || ""} testid={`pay-extra-${i}`} />
              ))}
              {pay.holder && <CopyField label="Titular" value={pay.holder} testid="pay-holder" />}
            </div>
            {(pay.qr_images || []).length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4">
                {pay.qr_images.map((src) => <img key={src} src={fileUrl(src)} alt="QR de pago" className="h-36 w-36 object-contain rounded-xl border border-black/10 bg-white" />)}
              </div>
            )}
            {(pay.advance_percent > 0 || pay.conditions) && (
              <div className="mt-4 p-3 rounded-xl bg-[var(--brand-accent)]/10 text-sm text-[var(--brand-accent)]" data-testid="pay-conditions">
                {pay.advance_percent > 0 && <div className="font-semibold">Adelanto requerido: {pay.advance_percent}% ({formatSoles(grandTotal * pay.advance_percent / 100)})</div>}
                {pay.conditions && <div>{pay.conditions}</div>}
              </div>
            )}
            <div className="mt-5">
              <Label>Método de pago</Label>
              <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger data-testid="checkout-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_METHODS.filter((m) => methods[m.v]).map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {shippingOptions.length > 0 && (
              <div className="mt-5">
                <Label>Entrega</Label>
                <Select value={form.shipping_option} onValueChange={(v) => set("shipping_option", v)}>
                  <SelectTrigger data-testid="checkout-shipping"><SelectValue placeholder="Elige cómo recibir tu pedido" /></SelectTrigger>
                  <SelectContent>
                    {shippingOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} · {o.cost ? formatSoles(o.cost) : "Gratis"}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedShip?.note && <p className="text-xs text-neutral-500 mt-1">{selectedShip.note}</p>}
                {/provincia/i.test(selectedShip?.name || "") && ship.province_instructions && <p className="text-xs text-neutral-500 mt-1">{ship.province_instructions}</p>}
                {ship.delivery_conditions && <p className="text-xs text-neutral-500 mt-1">{ship.delivery_conditions}</p>}
              </div>
            )}
            <div className="mt-5">
              <Label>Comprobante (opcional)</Label>
              <label className="mt-2 flex items-center gap-3 px-4 py-3 bg-neutral-50 border border-dashed border-black/15 rounded-xl cursor-pointer hover:border-[var(--brand-primary)]">
                <Upload size={18} />
                <span className="text-sm">
                  {uploading ? "Subiendo..." : receiptPath ? "Comprobante cargado ✓" : "Subir foto o PDF"}
                </span>
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleReceipt} data-testid="checkout-receipt-input" />
              </label>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-6 border border-black/5 sticky top-24">
            <h3 className="font-serif text-xl font-semibold mb-3">Tu pedido</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {cart.map((i) => {
                const u = cartItemPrice(i);
                return (
                  <div key={i.key || i.product_id} className="flex justify-between text-sm">
                    <span className="truncate pr-2">{i.qty}× {i.name}{i.variant ? ` (${i.variant})` : ""}</span>
                    <span className="font-semibold whitespace-nowrap">{formatSoles(u * i.qty)}</span>
                  </div>
                );
              })}
            </div>
            {selectedShip && (
              <div className="flex justify-between text-sm mt-3 text-neutral-600">
                <span>Envío · {selectedShip.name}</span>
                <span>{formatSoles(selectedShip.cost || 0)}</span>
              </div>
            )}
            <div className="border-t border-black/10 mt-4 pt-4 flex justify-between font-serif text-2xl font-bold">
              <span>Total</span>
              <span className="text-[var(--brand-primary)]" data-testid="checkout-total">{formatSoles(grandTotal)}</span>
            </div>
            <Button
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full mt-5 h-12 rounded-full font-semibold"
              data-testid="checkout-submit"
            >
              {submitting ? "Procesando..." : "Generar pedido FR-"}
            </Button>
            <p className="text-xs text-neutral-500 mt-3 text-center">
              Se generará un código único FR-XXXXXX y podrás enviarlo por WhatsApp.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
